import { css } from '@emotion/react';
import { Fragment, useState, type ReactNode } from 'react';
import Title from 'components/Title/Title';
import Page from 'components/Page/Page';
import LoadingScreen from 'components/LoadingScreen/LoadingScreen';
import ErrorScreen from 'components/ErrorScreen/ErrorScreen';

import StateBadge from './StateBadge/StateBadge';
import PriorityBandSelector from './PriorityBandSelector/PriorityBandSelector';
import PauseResumeButtons from './PauseResumeButtons';
import TaskGraph from './TaskGraph/TaskGraph';
import ExpandableDetails from './ExpandableDetails/ExpandableDetails';
import JsonViewer from './JsonViewer/JsonViewer';
import TaskDetails from './TaskDetails/TaskDetails';
import DetailMeta, { TimestampValue } from './DetailMeta';
import RunDagPanel from './RunDagPanel';
import UserFullName from 'components/UserFullName/UserFullName';
import RunStateLabel from 'pages/WorkflowRunsPage/Runs/RunStateLabel';
import CancelButton, { countTaskStates } from 'pages/WorkflowRunsPage/Runs/CancelButton';
import Files from 'pages/WorkflowRunsPage/Files/Files';

import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CopyToClipboardButton from 'components/CopyToClipboardButton/CopyToClipboardButton';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';

import { useParams } from 'react-router-dom';
import bulkResetFailedTasks from 'store/bulkResetFailedTasks';
import bulkResetOrphanedTasks from 'store/bulkResetOrphanedTasks';
import bulkResetTasks from 'store/bulkResetTasks';
import killTasks from 'store/killTasks';
import KillConfirmDialog from './KillConfirmDialog';
import fetchRunDetails from 'store/fetchRunDetails';
import fetchWorkflowProgress from 'store/fetchWorkflowProgress';
import useAutofetchedRunDetailsAsync from 'store/useAutofetchedRunDetailsAsync';
import useAutofetchedWorkflowProgressAsync from 'store/useAutofetchedWorkflowProgressAsync';

import { unique } from 'utils/array';

import RunState, { supportedRunStates } from 'types/RunState';

export default function WorkflowRunDetailsPage() {
  const { runId } = useParams() as { runId: string };
  const runDetailsAsync = useAutofetchedRunDetailsAsync(runId);
  const workflowProgressAsync = useAutofetchedWorkflowProgressAsync(runId);

  const [expandWorkflowDetails, setExpandWorkflowDetails] = useState(true);
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);

  // Multi-select includes pseudo-IDs for summary/pagination nodes that aren't real tasks.
  const realSelectedTaskIds = selectedTaskIds.filter(
    (id) => !id.startsWith('summary:') && !id.startsWith('page-prev:') && !id.startsWith('page-next:'),
  );

  // Auto-collapse details when a task is selected, expand when all
  // deselected — but allow the user to override via the accordion. Only
  // react on the 0↔nonzero transition so that adding/removing tasks while
  // the user has manually toggled the accordion doesn't fight them.
  // Adjusting state during render per the React docs' previous-prop pattern.
  const hasSelection = realSelectedTaskIds.length > 0;
  const [prevHasSelection, setPrevHasSelection] = useState(hasSelection);
  if (prevHasSelection !== hasSelection) {
    setPrevHasSelection(hasSelection);
    setExpandWorkflowDetails(!hasSelection);
  }

  const [columnMode, setColumnMode] = useState<'DAG' | 'DETAILS' | 'BOTH'>('BOTH');
  const [resettingAll, setResettingAll] = useState(false);
  const [resettingOrphaned, setResettingOrphaned] = useState(false);
  const [resettingSelected, setResettingSelected] = useState(false);
  // Snapshot the kill targets when the dialog opens: the 10s progress poll can
  // move a selected task out of RUNNING while the dialog is up, and the dialog
  // must keep asking about — and kill — the tasks the user was shown.
  const [confirmKillTaskIds, setConfirmKillTaskIds] = useState<string[] | null>(null);
  const [resetMessage, setResetMessage] = useState<string | null>(null);

  // Re-fetch /progress in addition to /runDetails so the graph colors and
  // legend reflect post-reset states immediately instead of waiting for the
  // next 10s poll. Clear the selection so the right-side cards, the graph's
  // visible selection (cleared by cy rebuild), and the "Reset task(s)" button
  // all stay in sync.
  const onResetSuccess = () => {
    fetchRunDetails(runId);
    fetchWorkflowProgress(runId);
    setSelectedTaskIds([]);
  };

  switch (runDetailsAsync.status) {
    case 'INIT':
    case 'PENDING':
      return <LoadingScreen />;
    case 'ERROR':
      return <ErrorScreen />;
    case 'SUCCESS':
    case 'REFRESH': {
      const runDetails = runDetailsAsync.data;

      // Drive the legend from the polled progress map when it has landed, so
      // no-longer-present states (e.g. RUNNING after everything completes)
      // drop out of the legend without a page reload. Fall back to the
      // one-shot runDetails snapshot while the first progress fetch is in
      // flight.
      const liveTaskStates: RunState[] = workflowProgressAsync.data?.tasks
        ? Object.values(workflowProgressAsync.data.tasks).map((t) => t.state)
        : Object.values(runDetails.task_states);

      const usedRunStates = liveTaskStates
        .filter(unique)
        .sort((a, b) => supportedRunStates.indexOf(a) - supportedRunStates.indexOf(b));

      const liveStateOf = (taskId: string): RunState | undefined =>
        workflowProgressAsync.data?.tasks?.[taskId]?.state ?? runDetails.task_states[taskId];
      const selectedRunningTaskIds = realSelectedTaskIds.filter((taskId) => liveStateOf(taskId) === 'RUNNING');

      return (
        <Fragment>
          <Title>{`${runDetails.name} | Tailor`}</Title>

          <Page>
            <div
              css={css`
                margin: 48px;
                margin-top: 24px;

                display: flex;
                flex-direction: column;
                flex-grow: 1;
              `}
            >
              <div
                css={css`
                  display: flex;
                  flex-wrap: wrap;
                  align-items: center;
                  gap: 8px 16px;

                  .MuiToggleButton-root {
                    padding: 4px 24px;
                  }

                  .MuiButton-root {
                    padding: 4px 24px;
                  }

                  .MuiOutlinedInput-root {
                    height: 36px;
                  }
                `}
              >
                <ToggleButtonGroup
                  exclusive
                  value={columnMode}
                  onChange={(e, mode) =>
                    // Don't change mode to null (we want exactly one selected at all times)
                    mode && setColumnMode(mode)
                  }
                  aria-label="View mode"
                >
                  <ToggleButton value="DAG" aria-label="Show only DAG" title="Show only DAG">
                    DAG
                  </ToggleButton>
                  <ToggleButton value="BOTH" aria-label="Show both DAG and Details" title="Show both DAG and Details">
                    Both
                  </ToggleButton>
                  <ToggleButton value="DETAILS" aria-label="Show only Details" title="Show only Details">
                    Details
                  </ToggleButton>
                </ToggleButtonGroup>
                <PriorityBandSelector runId={runDetails.id} priority={runDetails.priority} />
                <PauseResumeButtons
                  runId={runDetails.id}
                  paused={runDetails.paused}
                  taskStates={liveTaskStates}
                  onMessage={setResetMessage}
                />
                <CancelButton
                  selectedRuns={[
                    {
                      id: runDetails.id,
                      name: runDetails.name,
                      state: runDetails.state,
                      task_state_count: countTaskStates(liveTaskStates),
                    },
                  ]}
                  variant="outlined"
                  onSuccess={() => {
                    fetchRunDetails(runId);
                    fetchWorkflowProgress(runId);
                  }}
                />
                {runDetails.worker_lost_task_ids?.length > 0 && (
                  <Button
                    variant="outlined"
                    color="warning"
                    disabled={resettingOrphaned}
                    onClick={() => {
                      setResettingOrphaned(true);
                      bulkResetOrphanedTasks(runDetails.project_id, runDetails.id)
                        .then((result) => {
                          setResetMessage(
                            result.recovered_count > 0
                              ? `Reset ${result.recovered_count} lost task(s)`
                              : 'No lost tasks to reset',
                          );
                          onResetSuccess();
                        })
                        .catch((err) => {
                          console.error('Lost task reset failed:', err);
                          setResetMessage('Reset failed');
                        })
                        .finally(() => setResettingOrphaned(false));
                    }}
                  >
                    {resettingOrphaned && <CircularProgress size={16} sx={{ mr: 1 }} />}
                    {resettingOrphaned ? 'Resetting\u2026' : 'Reset lost tasks'}
                  </Button>
                )}
                {liveTaskStates.includes('FAILED' as RunState) && (
                  <Button
                    variant="outlined"
                    disabled={resettingAll}
                    onClick={() => {
                      setResettingAll(true);
                      bulkResetFailedTasks(runDetails.project_id, runDetails.id)
                        .then((result) => {
                          setResetMessage(
                            result.recovered_count > 0
                              ? `Reset ${result.recovered_count} failed task(s)`
                              : 'No failed tasks to reset',
                          );
                          onResetSuccess();
                        })
                        .catch((err) => {
                          console.error('Bulk reset failed:', err);
                          setResetMessage('Reset failed');
                        })
                        .finally(() => setResettingAll(false));
                    }}
                  >
                    {resettingAll && <CircularProgress size={16} sx={{ mr: 1 }} />}
                    {resettingAll ? 'Resetting\u2026' : 'Reset All Failed Tasks'}
                  </Button>
                )}
                <Button
                  variant="outlined"
                  disabled={realSelectedTaskIds.length === 0 || resettingSelected}
                  onClick={() => {
                    setResettingSelected(true);
                    bulkResetTasks(runDetails.project_id, runDetails.id, realSelectedTaskIds)
                      .then((result) => {
                        setResetMessage(
                          result.recovered_count > 0 ? `Reset ${result.recovered_count} task(s)` : 'No tasks reset',
                        );
                        onResetSuccess();
                      })
                      .catch((err) => {
                        console.error('Reset selected tasks failed:', err);
                        setResetMessage('Reset failed');
                      })
                      .finally(() => setResettingSelected(false));
                  }}
                >
                  {resettingSelected && <CircularProgress size={16} sx={{ mr: 1 }} />}
                  {resettingSelected ? 'Resetting\u2026' : 'Reset task(s)'}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  disabled={selectedRunningTaskIds.length === 0}
                  title="Terminate the selected running task(s) on their workers; they end FAILED and can be reset"
                  onClick={() => setConfirmKillTaskIds(selectedRunningTaskIds)}
                >
                  Kill task(s)
                </Button>
                {confirmKillTaskIds && (
                  <KillConfirmDialog
                    taskIds={confirmKillTaskIds}
                    onClose={() => setConfirmKillTaskIds(null)}
                    onConfirm={() =>
                      killTasks(runDetails.project_id, runDetails.id, confirmKillTaskIds).then((result) => {
                        setResetMessage(
                          result.killed_count > 0
                            ? `Kill requested for ${result.killed_count} task(s)`
                            : 'No tasks killed',
                        );
                        onResetSuccess();
                      })
                    }
                  />
                )}
              </div>
              {runDetails.local_files_recycled_utc && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Local files were recycled off their worker node on{' '}
                  {new Date(runDetails.local_files_recycled_utc).toLocaleString()}. Tasks that used them fail if reset
                  on their own — reset from the task that produced the files instead.
                </Alert>
              )}
              {runDetails.paused && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Workflow paused — READY and WAITING tasks are held and will not start until the workflow is resumed.
                  Running tasks that support pause checkpoint and hold; other running tasks finish their current run.
                </Alert>
              )}
              <DagColumns>
                {['DAG', 'BOTH'].includes(columnMode) && (
                  <RunDagPanel>
                    <TaskGraph
                      runDetails={runDetails}
                      setSelectedTaskIds={setSelectedTaskIds}
                      progressByTaskId={workflowProgressAsync.data?.tasks}
                    />
                    <div
                      css={css`
                        flex-shrink: 0;
                        padding: 24px;
                        display: flex;
                        flex-wrap: wrap;
                        grid-gap: 12px;
                      `}
                    >
                      {usedRunStates.map((state: RunState) => (
                        <StateBadge key={state} state={state} />
                      ))}
                      {runDetails.paused && (
                        <span
                          css={css`
                            display: inline-block;
                            padding: 8px;
                            font-size: 14px;
                            line-height: 18px;
                            letter-spacing: 0.08em;
                            color: #333;
                            background: #fff;
                            border: 3px dashed #b56552;
                          `}
                        >
                          HELD — will not start while the workflow is paused
                        </span>
                      )}
                      {runDetails.worker_lost_task_ids?.length > 0 && (
                        <span
                          css={css`
                            display: inline-block;
                            padding: 8px;
                            font-size: 21px;
                            line-height: 18px;
                            letter-spacing: 0.08em;
                            color: #333;
                            background: #fff;
                            border: 3px dashed #c62828;
                          `}
                        >
                          WORKER LOST
                        </span>
                      )}
                    </div>
                  </RunDagPanel>
                )}

                {['DETAILS', 'BOTH'].includes(columnMode) && (
                  <div>
                    <Accordion expanded={expandWorkflowDetails} onChange={() => setExpandWorkflowDetails((on) => !on)}>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="run-details-content"
                        id="run-details-header"
                      >
                        <div
                          css={css`
                            padding: 0 8px 0 12px;
                            flex-grow: 1;
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                          `}
                        >
                          <Typography
                            component="h1"
                            variant="h6"
                            css={css`
                              display: inline-flex;
                              align-items: center;
                            `}
                          >
                            {runDetails.id} {runDetails.name}
                            <span
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                              }}
                            >
                              <CopyToClipboardButton data={runDetails.id} size="small" subject="workflow ID" />
                            </span>
                          </Typography>
                          <Typography component="h1" variant="h6" style={{ fontSize: 10, letterSpacing: 1.5 }}>
                            WORKFLOW
                          </Typography>
                        </div>
                      </AccordionSummary>
                      <AccordionDetails style={{ display: 'block' }}>
                        <DetailMeta
                          rows={[
                            { label: 'State', value: <RunStateLabel state={runDetails.state} /> },
                            { label: 'Created', value: <TimestampValue iso={runDetails.created_utc} /> },
                            { label: 'Updated', value: <TimestampValue iso={runDetails.updated_utc} /> },
                            { label: 'User', value: <UserFullName id={runDetails.user_id} /> },
                          ]}
                        />
                        <ExpandableDetails
                          summary="Inputs"
                          contentPlaceholder="Inputs as json (only rendered when open)"
                          copyJson={runDetails.inputs}
                          renderContent={() => <JsonViewer json={runDetails.inputs} />}
                        />
                        <ExpandableDetails
                          summary="Outputs"
                          contentPlaceholder="Outputs as json (only rendered when open)"
                          copyJson={runDetails.outputs}
                          renderContent={() => <JsonViewer json={runDetails.outputs} />}
                        />
                        <ExpandableDetails
                          summary="File tags"
                          contentPlaceholder="File tags as json (only rendered when open)"
                          copyJson={runDetails.files}
                          renderContent={() => <JsonViewer json={runDetails.files} />}
                        />
                        <ExpandableDetails
                          summary="Local files"
                          contentPlaceholder="Local files as json (only rendered when open)"
                          copyJson={runDetails.local_files ?? {}}
                          renderContent={() => <JsonViewer json={runDetails.local_files ?? {}} />}
                        />
                        <ExpandableDetails
                          summary="DAG"
                          contentPlaceholder="DAG as json (only rendered when open)"
                          copyJson={runDetails.dag}
                          renderContent={() => <JsonViewer json={runDetails.dag} />}
                        />
                      </AccordionDetails>
                    </Accordion>

                    {realSelectedTaskIds.map((taskId) => (
                      <TaskDetails key={taskId} runId={runId} taskId={taskId} wfPaused={runDetails.paused} />
                    ))}
                  </div>
                )}
              </DagColumns>
              <div
                css={css`
                  margin-top: 24px;
                `}
              >
                <Files runId={runDetails.id} />
              </div>
            </div>
          </Page>
          <Snackbar
            open={resetMessage !== null}
            autoHideDuration={4000}
            onClose={() => setResetMessage(null)}
            message={resetMessage}
          />
        </Fragment>
      );
    }
  }
}

function DagColumns({ children }: { children: ReactNode }) {
  return (
    <div
      css={css`
        margin-top: 24px;

        @media (max-width: 767px) {
          margin: 12px;
        }

        flex-grow: 1;
        display: flex;
        grid-gap: 24px;

        > * {
          min-width: min(calc(100vw - 2 * 48px), 580px);
          width: 0;
        }

        > *:first-of-type {
          flex-grow: 3;
        }

        > *:nth-of-type(2) {
          flex-grow: 1;
        }

        .run-dag-panel {
          height: calc(100svh - 224px);
          overflow: hidden;
          position: sticky;
          top: 16px;
          align-self: flex-start;
        }

        @media (max-width: 1280px /* 1280 = 2*580 + 2*48 + 24 – Max width with 2 columns */) {
          flex-direction: column;

          .run-dag-panel {
            position: relative;
            top: auto;
            align-self: unset;
          }
          > * {
            width: unset;
          }
        }
      `}
    >
      {children}
    </div>
  );
}
